import { useState } from 'react';
import { CheckCircle, XCircle, Heart, Flame } from 'lucide-react';
import { motion } from 'framer-motion';

/**
 * Faz 3: Interactive ders icin Duolingo tarzi quiz oynatici.
 * Props:
 *   questions: [{ question, options[], correct_answer, explanation? }]
 *   lang: 'tr' | 'en'
 *   onComplete(score, correctCount, total): tum sorular bitti, score >= passing
 *   onFail(correctCount, total): canlar bitti
 */
export default function LessonQuizPlayer({ questions, lang, onComplete, onFail }) {
  const [currentIdx, setCurrentIdx] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [correctCount, setCorrectCount] = useState(0);
  const [lives, setLives] = useState(5);
  const [streak, setStreak] = useState(0);
  const [finished, setFinished] = useState(false);

  const totalQuestions = questions?.length || 0;
  const q = questions?.[currentIdx];

  if (!q || finished) return null;

  const submitAnswer = () => {
    if (!selectedOption || showResult) return;
    const isCorrect = selectedOption === q.correct_answer;
    setShowResult(true);
    if (isCorrect) {
      setCorrectCount(c => c + 1);
      setStreak(s => s + 1);
    } else {
      setLives(l => l - 1);
      setStreak(0);
    }
  };

  const nextQuestion = () => {
    const wasCorrect = selectedOption === q.correct_answer;
    const nextLives = wasCorrect ? lives : lives - 1;

    if (nextLives <= 0) {
      setFinished(true);
      if (onFail) onFail(correctCount + (wasCorrect ? 1 : 0), totalQuestions);
      return;
    }

    if (currentIdx + 1 >= totalQuestions) {
      const finalCorrect = correctCount;
      const score = Math.floor((finalCorrect / totalQuestions) * 100);
      setFinished(true);
      if (onComplete) onComplete(score, finalCorrect, totalQuestions);
      return;
    }

    setCurrentIdx(i => i + 1);
    setSelectedOption(null);
    setShowResult(false);
  };

  return (
    <div
      className="bg-white rounded-2xl p-6 border-2 border-gray-200"
      data-testid="lesson-quiz-player"
    >
      {/* Ust bar: can + ilerleme + streak */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-1" data-testid="quiz-lives">
          {[...Array(5)].map((_, i) => (
            <Heart
              key={i}
              size={20}
              className={i < lives ? 'text-red-500 fill-red-500' : 'text-gray-300'}
            />
          ))}
        </div>
        <div className="text-sm font-bold" data-testid="quiz-progress">
          {currentIdx + 1} / {totalQuestions}
        </div>
        {streak > 0 ? (
          <div className="flex items-center gap-1 text-sm font-bold text-orange-600" data-testid="quiz-streak">
            <Flame size={14} /> {streak}
          </div>
        ) : (
          <div className="w-10" />
        )}
      </div>

      {/* Soru */}
      <h2 className="text-lg font-bold mb-4" data-testid="quiz-question">
        {q.question}
      </h2>

      {/* Seçenekler */}
      <div className="space-y-2 mb-4">
        {(q.options || []).map((opt, i) => {
          const isSelected = selectedOption === opt;
          const isCorrectOption = opt === q.correct_answer;
          const showStatus = showResult;
          let classes = 'border-gray-300 hover:border-gray-400';
          if (showStatus && isCorrectOption) classes = 'border-green-500 bg-green-50';
          else if (showStatus && isSelected && !isCorrectOption) classes = 'border-red-500 bg-red-50';
          else if (isSelected) classes = 'border-blue-500 bg-blue-50';
          return (
            <button
              key={i}
              onClick={() => !showResult && setSelectedOption(opt)}
              disabled={showResult}
              className={`w-full p-3 rounded-xl border-2 text-left transition ${classes} disabled:cursor-default`}
              data-testid={`quiz-option-${i}`}
            >
              {opt}
              {showStatus && isCorrectOption && (
                <CheckCircle size={18} className="inline ml-2 text-green-600" />
              )}
              {showStatus && isSelected && !isCorrectOption && (
                <XCircle size={18} className="inline ml-2 text-red-600" />
              )}
            </button>
          );
        })}
      </div>

      {/* Açıklama (cevaptan sonra) */}
      {showResult && q.explanation && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 text-sm"
          data-testid="quiz-explanation"
        >
          {q.explanation}
        </motion.div>
      )}

      {/* Butonlar */}
      {!showResult ? (
        <button
          onClick={submitAnswer}
          disabled={!selectedOption}
          className="w-full py-3 bg-blue-500 text-white rounded-xl font-bold disabled:opacity-50"
          data-testid="quiz-submit-btn"
        >
          {lang === 'tr' ? 'Cevapla' : 'Submit'}
        </button>
      ) : (
        <button
          onClick={nextQuestion}
          className="w-full py-3 bg-green-500 text-white rounded-xl font-bold"
          data-testid="quiz-next-btn"
        >
          {currentIdx + 1 >= totalQuestions
            ? (lang === 'tr' ? 'Bitir' : 'Finish')
            : (lang === 'tr' ? 'Sonraki Soru' : 'Next Question')}
        </button>
      )}
    </div>
  );
}
